import React, { useCallback, useEffect, useState } from 'react'
import SimpleBar from 'simplebar-react'
import { XClose, Trash01, Send01 } from '@untitled-ui/icons-react'
import {
  MyButton,
  MyDoubleCard,
  WhatsApp,
  MyAvatar,
  myToaster,
  MyConfirmModal,
} from '@interstellar-component'
import { format } from 'date-fns'
import { useReportEnquiry } from '../Context'
import Service from '../service'
import MySLAStatusChip from './MySLAStatusChip'
import TruncatedText from '../../../components/TruncatedText'
import TruncateRow from './TruncateRow'

const STATUS_DISPLAY = {
  awaiting_form: 'Awaiting Form',
  awaiting_admin: 'Awaiting Admin Approval',
  awaiting_consent: 'Awaiting Consent',
  verification: 'Verification',
  form_revision: 'Form Revision',
  sent_to_clik: 'Sent to CLIK',
  completed: 'Completed',
  failed: 'Failed',
  canceled: 'Canceled',
}

const REPEAT_DISPLAY = {
  none: 'None',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annually: 'Semiannual',
  annually: 'Annual',
}

function formatDate(dateStr) {
  if (!dateStr) return '-'
  try {
    return format(new Date(dateStr), 'MMM d, yyyy')
  } catch {
    return dateStr
  }
}

export default function AwaitingConsentSlider({ data }) {
  const { popSlider, getList } = useReportEnquiry()
  const [cancelModal, setCancelModal] = useState(false)
  const [resendModal, setResendModal] = useState(false)
  const [detail, setDetail] = useState(null)
  const [loading, setLoading] = useState(false)

  const enquiryId = data?._raw?.id || data?.id

  const fetchDetail = useCallback(() => {
    if (!enquiryId) return
    setLoading(true)
    Service.show(enquiryId)
      .then((res) => setDetail(res.data))
      .catch(myToaster)
      .finally(() => setLoading(false))
  }, [enquiryId])

  useEffect(() => {
    fetchDetail()
  }, [fetchDetail])

  const d = detail || {}
  // BE wraps form fields under general_information for awaiting_form status;
  // older code paths may keep them flat, so accept either shape.
  const gi = d.general_information || d
  const target = gi.target || d.target || {}
  const company = gi.company || d.company || {}
  const creator = d.creator || {}
  const activities = d.activity || []
  const categoryKey = gi.category || d.category

  const displayData = {
    order: d.reference_number || data?.order || '-',
    orderDate: formatDate(d.created_at),
    name: target.full_name || data?.name || '-',
    employeeId: target.code || data?.employeeId || '-',
    category: categoryKey === 'employee' ? 'Employee' : 'Candidate',
    level: gi.employment_level?.name || d.employment_level?.name || '-',
    whatsapp: gi.phone || target.phone || '-',
    email: gi.email || target.email || '-',
    entity: company.name || data?.entity || '-',
    consentExpiry: formatDate(gi.consent_expiry ?? d.consent_expiry),
    repeatEvery:
      REPEAT_DISPLAY[gi.repeat_every ?? d.repeat_every] ||
      gi.repeat_every ||
      d.repeat_every ||
      '-',
    position: target.position || gi.position?.name || d.position?.name || '-',
    status: STATUS_DISPLAY[d.status] || d.status || 'Awaiting Form',
    creatorName: creator.name || 'System',
    creatorAvatar: creator.avatar_url,
  }

  const handleClose = () => popSlider()

  const handleCancel = async () => {
    await Service.cancel(enquiryId)
      .then((res) => {
        myToaster(res)
        getList()
        popSlider()
      })
      .catch(myToaster)
  }

  const handleResend = async () => {
    await Service.resend(enquiryId)
      .then((res) => {
        myToaster(res)
        setResendModal(false)
        fetchDetail()
      })
      .catch(myToaster)
  }

  return (
    <div className="flex h-screen w-[420px] flex-col bg-white shadow-xl">
      {/* Header */}
      <header className="relative flex items-start gap-x-4 px-6 py-6 border-b border-gray-100 pb-4">
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-[12px] top-[12px] flex h-10 w-10 items-center justify-center rounded-lg p-2 text-gray-400 hover:bg-gray-50 active:bg-gray-100"
        >
          <XClose size={24} stroke="currentColor" />
        </button>

        <div className="flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-1">
            <h2 className="text-xl font-semibold text-gray-900">{displayData.order}</h2>
            <p className="text-sm text-gray-500">{displayData.orderDate}</p>
          </div>
          <div className="w-max">
            <MySLAStatusChip status={displayData.status} />
          </div>
        </div>
      </header>

      {/* Scrollable body */}
      <section className="flex-1 overflow-hidden bg-gray-50/30">
        <SimpleBar forceVisible="y" style={{ maxHeight: '100%' }}>
          <div className="flex flex-col gap-6 px-6 py-6">
            {loading && (
              <div className="flex items-center justify-center py-8">
                <p className="text-sm text-gray-500">Loading...</p>
              </div>
            )}

            {!loading && (
              <>
                {/* General Information Card */}
                <MyDoubleCard heading="General Information" innerClassName="p-0">
                  <div className="divide-y divide-gray-100">
                    {/* Target */}
                    <div className="flex items-center justify-between gap-3 p-4">
                      <span className="shrink-0 text-sm text-gray-500">Target</span>
                      <div className="flex max-w-[70%] min-w-0 items-center gap-3">
                        <MyAvatar name={displayData.name} size={40} />
                        <div className="flex min-w-0 flex-1 flex-col">
                          <TruncatedText
                            text={displayData.name}
                            className="text-right text-sm font-semibold text-gray-900"
                          />
                          <TruncatedText
                            text={displayData.employeeId}
                            className="text-right text-sm font-medium text-gray-900"
                          />
                          <TruncatedText
                            text={displayData.position}
                            className="text-right text-xs text-gray-500"
                          />
                        </div>
                      </div>
                    </div>

                    <TruncateRow label="Category" value={displayData.category} />
                    <TruncateRow label="Level" value={displayData.level} />
                    <TruncateRow
                      label="WhatsApp"
                      value={displayData.whatsapp}
                      icon={<WhatsApp className="size-4 shrink-0 text-green-500" />}
                    />
                    <TruncateRow label="Email address" value={displayData.email} />
                    <TruncateRow label="Entity" value={displayData.entity} />
                    <TruncateRow label="Consent expiry" value={displayData.consentExpiry} />
                    <TruncateRow label="Repeat every" value={displayData.repeatEvery} />
                  </div>
                </MyDoubleCard>

                {/* Activity Card */}
                <MyDoubleCard heading="Activity" innerClassName="p-4">
                  <div className="flex flex-col gap-0 antialiased">
                    {activities.map((act, idx) => (
                      <div key={act.id} className={`relative flex gap-4 ${idx < activities.length - 1 ? 'pb-8' : ''}`}>
                        {idx < activities.length - 1 && (
                          <div className="absolute left-[20px] top-[40px] h-[calc(100%-40px)] w-0.5 bg-gray-200" />
                        )}
                        <MyAvatar
                          name={act.actor_name}
                          src={act.actor?.avatar_url}
                          size={40}
                        />
                        <div className="flex flex-1 min-w-0 flex-col gap-1">
                          <div className="flex items-start gap-2">
                            <span className="flex-1 min-w-0 text-sm font-semibold text-gray-900">
                              {act.event_type?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                            </span>
                            <span className="shrink-0 whitespace-nowrap text-xs text-gray-500">
                              {formatDate(act.occurred_at)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 leading-relaxed [overflow-wrap:anywhere]">
                            {act.event_description}
                          </p>
                        </div>
                      </div>
                    ))}
                    {activities.length === 0 && (
                      <p className="text-sm text-gray-400">No activity yet</p>
                    )}
                  </div>
                </MyDoubleCard>
              </>
            )}
          </div>
        </SimpleBar>
      </section>

      {/* Footer */}
      <footer className="flex items-center justify-between border-t border-gray-200 px-6 py-4">
        <MyButton
          color="error"
          variant="link"
          size="md"
          customClassname="gap-2 px-0"
          onClick={() => setCancelModal(true)}
        >
          <Trash01 size={20} />
          Cancel request
        </MyButton>

        <MyButton
          color="secondary"
          variant="outlined"
          size="md"
          customClassname="gap-2"
          onClick={() => setResendModal(true)}
        >
          <Send01 size={20} className="text-gray-500" />
          Resend form
        </MyButton>
      </footer>

      <MyConfirmModal
        negativeActionWord="Back"
        positiveActionWord="Cancel request"
        open={cancelModal}
        onClose={() => setCancelModal(false)}
        onConfirm={handleCancel}
        title="Cancel request"
        description="Are you sure you want to cancel this enquiry request? This action cannot be undone."
        confirmText="Cancel request"
        confirmColor="error"
      />

      <MyConfirmModal
        negativeActionWord="Cancel"
        positiveActionWord="Resend form"
        open={resendModal}
        onClose={() => setResendModal(false)}
        onConfirm={handleResend}
        title="Resend form"
        description="A new invitation link will be sent to the applicant and the previous link will be invalidated. Continue?"
        confirmText="Resend form"
        confirmColor="primary"
      />
    </div>
  )
}
